import { authApiSlice } from "./authApiSlice";

export const parentApiSlice = authApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getParentStudents: builder.query({
      query: () => "/parent/students",
      providesTags: ["ParentStudents"],
    }),

    getStudentInvoices: builder.query({
      query: (studentId) => `/fees/invoice/student/${studentId}`,
      providesTags: (result, error, studentId) => [
        { type: "StudentInvoices", id: studentId },
      ],
    }),

    getStudentReports: builder.query({
      query: (studentId) => `/report/student?student_id=${studentId}`,
      providesTags: (result, error, studentId) => [
        { type: "StudentReports", id: studentId },
      ],
    }),

    getStudent: builder.query({
      query: (studentId) => `/student/${studentId}`,
      providesTags: (result, error, studentId) => [
        { type: "Student", id: studentId },
      ],
    }),

    getMyStudentProfile: builder.query({
      query: () => "/student/profile",
      providesTags: ["MyStudentProfile"],
    }),

    removeInvoiceItem: builder.mutation({
      query: ({ invoiceId, item_id }) => ({
        url: `/fees/invoice/${invoiceId}/remove-item`,
        method: "PATCH",
        body: { item_id },
      }),
      invalidatesTags: (result, error, { studentId }) => [
        { type: "StudentInvoices", id: studentId },
      ],
    }),

    addInvoiceEvidence: builder.mutation({
      query: ({ invoiceId, file_url, note }) => ({
        url: `/fees/invoice/${invoiceId}/evidence`,
        method: "POST",
        body: { file_url, note },
      }),
      invalidatesTags: (result, error, { studentId }) => [
        { type: "StudentInvoices", id: studentId },
      ],
    }),

    removeInvoiceEvidence: builder.mutation({
      query: ({ invoiceId, evidenceId }) => ({
        url: `/fees/invoice/${invoiceId}/evidence/${evidenceId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { studentId }) => [
        { type: "StudentInvoices", id: studentId },
      ],
    }),

    getSchoolBankAccount: builder.query({
      query: () => `/fees/account/school`,
      providesTags: ["SchoolBankAccount"],
    }),

    getSchoolBankAccounts: builder.query({
      query: () => `/fees/bank-accounts/school`,
      providesTags: ["SchoolBankAccounts"],
    }),

    getStudentQuizHistory: builder.query({
      query: ({ studentId, page = 1, perPage = 10 }) =>
        `/quiz/student/${studentId}/history?page=${page}&per_page=${perPage}`,
      providesTags: (result, error, { studentId }) => [
        { type: "StudentQuizHistory", id: studentId },
      ],
    }),

    getStudentAnalytics: builder.query({
      query: ({ studentId, range = 'all' }) =>
        `/quiz/student/${studentId}/analytics?range=${range}`,
      providesTags: (result, error, { studentId }) => [
        { type: "StudentAnalytics", id: studentId },
      ],
    }),

    // POST /subscription/parent-ai-credits/purchase
    purchaseParentAiCredits: builder.mutation({
      query: (body) => ({
        url: "/subscription/parent-ai-credits/purchase",
        method: "POST",
        body,
      }),
    }),

    // POST /subscription/parent-ai-credits/verify
    verifyParentAiCredits: builder.mutation({
      query: (body) => ({
        url: "/subscription/parent-ai-credits/verify",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ParentAIBalance"],
    }),

    // GET /subscription/parent-ai-credits/balance
    getParentAiBalance: builder.query({
      query: () => "/subscription/parent-ai-credits/balance",
      providesTags: ["ParentAIBalance"],
    }),

    // GET /subscription/parent-ai-credits/history
    getParentAiHistory: builder.query({
      query: (params = {}) => ({
        url: "/subscription/parent-ai-credits/history",
        params,
      }),
      providesTags: ["ParentAIBalance"],
    }),

    // PATCH /subscription/parent-ai-credits/settings
    updateParentAiSettings: builder.mutation({
      query: (body) => ({
        url: "/subscription/parent-ai-credits/settings",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ParentAIBalance"],
    }),

    // GET /subscription/parent-ai-credits/usage
    getParentAiUsage: builder.query({
      query: (params = {}) => ({
        url: "/subscription/parent-ai-credits/usage",
        params,
      }),
      providesTags: ["ParentAIBalance"],
    }),

    // POST /report/unlock — parent pays credits to unlock a locked report
    unlockReport: builder.mutation({
      query: (body) => ({
        url: "/report/unlock",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { studentId }) =>
        studentId ? [{ type: "StudentReports", id: studentId }] : ["StudentReports"],
    }),
  }),
});

export const {
  useGetParentStudentsQuery,
  useGetStudentInvoicesQuery,
  useGetStudentReportsQuery,
  useGetStudentQuery,
  useGetMyStudentProfileQuery,
  useRemoveInvoiceItemMutation,
  useAddInvoiceEvidenceMutation,
  useRemoveInvoiceEvidenceMutation,
  useGetSchoolBankAccountQuery,
  useGetSchoolBankAccountsQuery,
  useGetStudentQuizHistoryQuery,
  useGetStudentAnalyticsQuery,
  usePurchaseParentAiCreditsMutation,
  useVerifyParentAiCreditsMutation,
  useGetParentAiBalanceQuery,
  useGetParentAiHistoryQuery,
  useUpdateParentAiSettingsMutation,
  useGetParentAiUsageQuery,
  useUnlockReportMutation,
} = parentApiSlice;
